<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore, infoBoxIdEnum, infoBoxToneEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { stringToColor, textColor } from '@/lib/utils'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'
import { useAvailability } from '@/composables/availability'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ConditionSlider from '@/components/shared/ConditionSlider.vue'
import QrcodeScanner from '@/components/shared/QrcodeScanner.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ServiceSelector from '@/components/shared/ServiceSelector.vue'
import RateCardInfo from '@/components/shared/RateCardInfo.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import WarehouseSelector from '@/components/shared/WarehouseSelector.vue'
import EquipmentOrderFields from '@/components/shared/EquipmentOrderFields.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import CreateModeToggle from '@/components/shared/CreateModeToggle.vue'
import ExchangeRatePanel from '@/components/shared/ExchangeRatePanel.vue'
import { pickExchangeRate, useExchangeRateEdit } from '@/composables/exchange-rate'
import { useChips } from '@/composables/chips'
import { buildTagPayload } from '@/composables/tag'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  isRented: {
    type: Boolean,
    required: false,
    default: false,
  },
  initialQuickCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  availability: {
    type: Array,
    required: false,
    default: () => [],
  },
})

const i18n = getI18n()
const t = i18n.t
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()
const { getTagChips, needQrScanChip, isVehicleChip, getServiceStructureTypeChip, archivedChip } = useChips()

const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { convertAvailability } = useAvailability()

const form = ref(null)
const quickCreate = ref(props.initialQuickCreate && !props.modelValue.id)
const quickStep = ref(1)
const cameraGalleryInput = ref(null)
const serializedParentGroups = ref([])
const isSerializedBlocked = computed(() => serializedParentGroups.value.length > 0 || editItem.value.is_vehicle)
const isSerializedDisabled = computed(() => !isPhysical.value || isSerializedBlocked.value)
const stockTypeInfoKey = computed(() => (editItem.value.is_serialized ? 'serialized' : 'generic'))
const serializedTooltip = computed(() => {
  if (editItem.value.is_vehicle) return i18n.t('common.vehicleRequiresSerialized')
  if (serializedParentGroups.value.length === 0) return null
  return i18n.t('common.serializedUsedInGroups', { groups: serializedParentGroups.value.join(', ') })
})
const editItem = ref(props.modelValue.id ? useClone(props.modelValue) : {
  condition_value: planningStore.equipmentConditionEnum.GOOD,
  is_rented: props.isRented,
  is_vehicle: false,
  is_serialized: false,
  need_qr_scan: false,
  internal_serial: '',
  availability_type: portalStore.serviceAvailabilityTypeEnum.USUALLY_AVAILABLE,
  structure_type: portalStore.serviceStructureTypeEnum.PHYSICAL,
  currency_ref: webSiteStore.getDefaultCurrency().id,
  availability: convertAvailability(props.availability),
  type: portalStore.serviceTypeEnum.EQUIPMENT,
  warehouse: webSiteStore.getDefaultWarehouse(),
  warehouse_quantity: 1,
  ordered: false,
  order_warehouse: null,
  expected_arrival_date: null,
  tags: [],
  components: [],
  rc_price: null,
  rc_cost: null,
  rc_currency_ref: webSiteStore.getDefaultCurrency()?.id,
  rc_unit_of_measure_ref: webSiteStore.getDefaultUnitOfMeasure()?.id,
  rc_vat_type_ref: webSiteStore.getDefaultVatType()?.id,
  ...useClone(props.modelValue),
})

const { isUpdated, isUpdating, original, setOriginal } = useIsUpdated(editItem)
const { onCurrencyChange: applyCurrencyExchangeRate, onExchangeRateChange } = useExchangeRateEdit(editItem, original)

const isPhysical = computed(() => editItem.value.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL)
const isGroup = computed(() =>
  editItem.value.structure_type === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL ||
  editItem.value.structure_type === portalStore.serviceStructureTypeEnum.GROUP_VIRTUAL
)
const isGroupPhysical = computed(() => editItem.value.structure_type === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL)

const normalizeComponent = (component) => {
  if (component == null) return null
  if (typeof component === 'number') {
    return { id: component, quantity: 1 }
  }

  if (component.id == null) return null

  const quantity = component.quantity !== '' && component.quantity != null ? Number(component.quantity) : 1
  return {
    id: component.id,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
  }
}

const normalizeComponents = (components) => (components || []).map(normalizeComponent).filter(Boolean)

const allEquipments = computed(() => planningStore.equipments || [])
const availableComponents = computed(() => {
  const selectedIds = new Set(normalizeComponents(editItem.value.components).map(component => component.id))
  const baseOptions = allEquipments.value.filter(
    e => e.id !== editItem.value.id && !e.archived && !selectedIds.has(e.id)
  )
  if (isGroupPhysical.value) {
    return baseOptions.filter(e => e.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL)
  }
  return baseOptions
})
const selectedComponentInput = ref(null)

const onStructureTypeChange = () => {
  showComponentValidation.value = false
  if (isPhysical.value) {
    editItem.value.is_rented = false
    editItem.value.is_vehicle = false
    editItem.value.components = []
  } else {
    // group: reset rental/vehicle/serialized flags, clean incompatible components
    editItem.value.is_rented = false
    editItem.value.is_vehicle = false
    editItem.value.is_serialized = false
    if (isGroupPhysical.value) {
      const physicalIds = new Set(
        allEquipments.value
          .filter(e => e.id !== editItem.value.id && e.structure_type === portalStore.serviceStructureTypeEnum.PHYSICAL)
          .map(e => e.id)
      )
      editItem.value.components = normalizeComponents(editItem.value.components).filter(component => physicalIds.has(component.id))
    }
  }
}

const filteredComponents = ref([])
const showComponentValidation = ref(false)

const onComponentFilter = (val, update) => {
  update(() => {
    const needle = (val || '').toLowerCase()
    filteredComponents.value = availableComponents.value
      .filter(e => e.name.toLowerCase().includes(needle))
  })
}

const addComponent = (serviceId) => {
  if (!serviceId) return
  const currentComponents = normalizeComponents(editItem.value.components)
  if (!currentComponents.some(component => component.id === serviceId)) {
    editItem.value.components = [...currentComponents, { id: serviceId, quantity: 1 }]
  }
  showComponentValidation.value = false
  selectedComponentInput.value = null
  filteredComponents.value = []
}

const removeComponent = (serviceId) => {
  editItem.value.components = normalizeComponents(editItem.value.components).filter(component => component.id !== serviceId)
}

const updateComponentQuantity = (serviceId, value) => {
  const quantity = value !== '' && value != null ? Number(value) : 1
  editItem.value.components = normalizeComponents(editItem.value.components).map(component =>
    component.id === serviceId
      ? { ...component, quantity: Number.isFinite(quantity) && quantity >= 1 ? quantity : 1 }
      : component
  )
}

const incrementComponentQuantity = (serviceId) => {
  const current = normalizeComponents(editItem.value.components).find(component => component.id === serviceId)
  updateComponentQuantity(serviceId, (current?.quantity || 1) + 1)
}

const decrementComponentQuantity = (serviceId) => {
  const current = normalizeComponents(editItem.value.components).find(component => component.id === serviceId)
  updateComponentQuantity(serviceId, Math.max(1, (current?.quantity || 1) - 1))
}

const componentItems = computed(() =>
  normalizeComponents(editItem.value.components)
    .map(component => {
      const equipment = allEquipments.value.find(e => e.id === component.id)
      return equipment ? { ...equipment, component_quantity: component.quantity } : null
    })
    .filter(Boolean)
)

const basicInfoHasError = computed(() => !editItem.value.name)
const componentsHasError = computed(() => isGroup.value && componentItems.value.length === 0)
const rentalHasError = computed(() => editItem.value.is_rented && (!editItem.value.rental_company || !(editItem.value.availability?.length > 0)))

const getEquipmentRightCaptionChips = (equipment) => {
  if (equipment?.condition_value == null) return []
  const color = planningStore.getConditionColor(equipment.condition_value)
  return [{
    name: planningStore.getConditionLabel(equipment.condition_value),
    color,
    textColor: textColor(color),
  }]
}

const getEquipmentRightChips = (equipment) => {
  const chips = []
  if (equipment?.is_rented) {
    chips.push({
      name: i18n.t('common.rented'),
      color: 'grey',
      size: 'sm',
    })
  }
  if (equipment?.archived) {
    chips.push(archivedChip)
  }
  if (equipment?.need_qr_scan) {
    chips.push(needQrScanChip)
  }
  if (equipment?.is_vehicle) {
    chips.push(isVehicleChip)
  }
  return chips
}

const getEquipmentName = (equipment) => planningStore.getServiceName(equipment)

const getComponentsRequiredMessage = () => `${i18n.t('common.components')}: ${i18n.t('common.required')}`

const validateComboComponents = ({ notify = false, focus = false } = {}) => {
  const isValid = !isGroup.value || componentItems.value.length > 0
  showComponentValidation.value = !isValid
  if (isValid) return true
  if (focus) {
    step.value = 2
  }
  if (notify) {
    notifyError(getComponentsRequiredMessage())
  }
  return false
}

onMounted(async () => {
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  await planningStore.fetchEquipmentsAsync({ all: true }, true)
  if (props.modelValue.id) {
    const [equipmentRes, serializedRes] = await Promise.all([
      planningStore.fetchEquipmentByIdAsync(props.modelValue.id),
      planningStore.checkEquipmentSerializedAsync(props.modelValue.id),
    ])
    editItem.value = { ...equipmentRes.data }
    editItem.value.condition = editItem.value.condition?.condition ?? planningStore.equipmentConditionEnum.GOOD
    editItem.value.availability = convertAvailability(editItem.value.availability)
    editItem.value.components = normalizeComponents(editItem.value.components)
    serializedParentGroups.value = serializedRes.data.groups || []
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else {
    // Prefill the code with the next database id (editable hint).
    editItem.value.code = String(await planningStore.fetchNextEquipmentIdAsync())
    setOriginal(editItem.value)
  }
})

const rentalCompanies = ref([])

const addServiceTag = async (value, doneFn) => {
  const existingTag = planningStore.getLocalizedServiceTags().find(t => t.name.toLowerCase() === value.toLowerCase())
  if (existingTag) {
    doneFn(existingTag.id)
    return
  }
  const created = (await planningStore.addServiceTagAsync(buildTagPayload(value, { name: value }))).data
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  doneFn(created.id)
}
const addPrimaryServiceTag = (value, doneFn) => addServiceTag(value, doneFn)
const localizedServiceAvailabilityTypes = portalStore.getLocalizedServiceAvailabilityTypes()
const localizedServiceStructureTypes = portalStore.getLocalizedServiceStructureTypes()
const equipmentStructureTypes = localizedServiceStructureTypes.filter(t =>
  t.id === portalStore.serviceStructureTypeEnum.PHYSICAL ||
  t.id === portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL
)

const isScanning = ref(false)
const scanningTimeout = ref(null)

onMounted(async() => {
  rentalCompanies.value = (await planningStore.fetchCompaniesAsync({all: true}, true))?.filter(i => i.is_rental)
})

const currentCurrency = computed(() => webSiteStore.currencies.find(i => i.id === editItem.value.currency_ref) || webSiteStore.getDefaultCurrency())


const onCurrencyChange = (val) => {
  applyCurrencyExchangeRate(val)
  if (!val) {
    editItem.value.cost = null
    editItem.value.buy_date = null
  } else {
    editItem.value.cost = editItem.value.cost || 0
    editItem.value.buy_date = editItem.value.buy_date || dayjs().format('YYYY-MM-DD')
  }
}

const onCostChange = (value) => {
  if (!editItem.value.currency_ref) {
    editItem.value.currency_ref = webSiteStore.getDefaultCurrency().id
  }
  editItem.value.cost = value
}

const onScan = () => (isScanning.value = true)

const onScanError = (error) => {
  console.error(error)
  notifyError(i18n.t('error.qrCodeNotFound'))
  isScanning.value = false
}

const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async(id) => { await planningStore.archiveEquipmentAsync(id) },
  onDelete: async(id, config) => { 
    await planningStore.deleteEquipmentAsync(id, config) 
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const onDetect = (codes) => {
  clearTimeout(scanningTimeout.value)
  try {
    const decodedValue = JSON.parse(codes[0].rawValue)
    notifySuccess(`${i18n.t('common.qrCodeDetected')}!`)
    editItem.value.barcode = decodedValue.rawValue || decodedValue
  } catch {
    notifyError(i18n.t('error.qrCodeNotFound'))
  } finally {
    isScanning.value = false
  }
}

const onRentalCompanySelected = (value) => {
  if (!value) {
    editItem.value.rental_company = null
  } else if (typeof value === 'string') {
    editItem.value.rental_company = { name: value, is_rental: true }
  } else {
    editItem.value.rental_company = rentalCompanies.value.find(i => i.id === value)
  }
}

const onUpdate = async() => {
  isUpdating.value = true
  const updateItem = { ...editItem.value }

  if (editItem.value.availability?.length > 0) {
    updateItem.availability = editItem.value.availability.map(i => ({
      start: dayjs(i?.start || i?.from || i).toISOString(),
      end: dayjs(i?.end || i?.to || i).toISOString(),
    }))
  }
  if (!editItem.value.avatar_file?.payload) {
    delete updateItem.avatar_file
  }

  if (updateItem.condition_comment === '') {
    delete updateItem.condition_comment
  }

  if (editItem.value.buy_date) {
    updateItem.buy_date = dayjs(editItem.value.buy_date).format('YYYY-MM-DD')
  }
  updateItem.components = normalizeComponents(editItem.value.components)

  try {
    const saved = await planningStore.upsertEquipmentAsync(updateItem)
    updateItem.id = updateItem.id || saved.data.id
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', updateItem)
    isUpdating.value = false
  } catch(error)  {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
    isUpdating.value = false
  } 
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const onIsRentedUpdate = (value) => {
  if (!value) {
    editItem.value.rental_company = null
    editItem.value.availability = []
  }
}

const onIsVehicleUpdate = (value) => {
  if (value) editItem.value.is_serialized = true
}

// Serialized items are tracked individually, so their stock quantity is locked to 1.
const onIsSerializedUpdate = (value) => {
  if (value) {
    editItem.value.warehouse_quantity = 1
  }
}

const onSubmit = async() => {
  const isValid = await form.value?.validate()
  if (!isValid || !validateComboComponents({ notify: true, focus: true })) {
    return
  }
  await onUpdate()
}

const onDownloadAttachment = async (file) => {
  try {
    await planningStore.downloadServiceFileAsync(file.id, file.name)
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editEquipment') : i18n.t('common.addEquipment')"
      @cancel="onCancel"
    >
      <template v-if="!editItem?.id" #right>
        <create-mode-toggle v-model="quickCreate" />
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm">
        <q-form ref="form">
          <q-stepper
            v-if="quickCreate"
            v-model="quickStep"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.quickCreate')"
              icon="mdi-flash"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  maxlength="20"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-8' : 'col-6'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <div :class="isMobile ? 'col-12' : 'col-4'" class="row items-center no-wrap">
                  <q-toggle
                    v-model="editItem.is_serialized"
                    :label="editItem.is_serialized ? i18n.t('common.isSerialized') : i18n.t('common.generic')"
                    color="primary"
                    :disable="editItem.is_vehicle"
                    @update:model-value="onIsSerializedUpdate"
                  />
                </div>
                <form-section-title :label="i18n.t('common.stock')" />
                <warehouse-selector
                  v-model="editItem.warehouse"
                  :kind="planningStore.warehouseKindEnum.PHYSICAL"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="editItem.ordered ? i18n.t('common.destinationWarehouse') : i18n.t('common.referenceWarehouse')"
                  required
                />
                <q-input
                  v-if="!editItem.is_serialized"
                  v-model.number="editItem.warehouse_quantity"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  type="number"
                  min="1"
                  step="1"
                  :label="i18n.t('common.initialQuantity')"
                  :rules="[val => (val != null && val > 0) || i18n.t('common.required')]"
                />
                <template v-else>
                  <q-input
                    v-model="editItem.serial_number"
                    :class="isMobile ? 'col-12' : 'col-3'"
                    :label="i18n.t('common.serialNumber')"
                    maxlength="20"
                    clearable
                  />
                  <q-input
                    v-model="editItem.internal_serial"
                    :class="isMobile ? 'col-12' : 'col-3'"
                    :label="i18n.t('common.internalSerial')"
                    maxlength="100"
                    clearable
                  />
                </template>
              </div>
              <equipment-order-fields
                v-model:ordered="editItem.ordered"
                v-model:order-warehouse="editItem.order_warehouse"
                v-model:expected-arrival-date="editItem.expected_arrival_date"
                :mobile="isMobile"
              />
              <stepper-navigation
                :finish="onSubmit"
                :finish-label="i18n.t('common.addEquipment')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
          <q-stepper
            v-else
            v-model="step"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && basicInfoHasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  maxlength="20"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-8' : 'col-4'"
                  :label="i18n.t('common.name')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-select
                  v-model="editItem.structure_type"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.structureType')"
                  option-value="id"
                  option-label="name"
                  :options="equipmentStructureTypes"
                  emit-value
                  map-options
                  required
                  :rules="[val => val != null || i18n.t('common.required')]"
                  @update:model-value="onStructureTypeChange"
                />
                <q-select
                  v-model="editItem.availability_type"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.availabilityType')"
                  option-value="id"
                  option-label="name"
                  :options="localizedServiceAvailabilityTypes"
                />
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <q-select
                  :model-value="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.tag', 2)"
                  :options="planningStore.getLocalizedServiceTags()"
                  option-value="id"
                  option-label="name"
                  use-input
                  use-chips
                  multiple
                  new-value-mode="add-unique"
                  @new-value="addPrimaryServiceTag"
                  @update:model-value="(value) => { editItem.tags = value }"
                >
                  <template #selected-item="chip">
                    <q-chip
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), 'margin': 0, 'margin-right': '.25rem' }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
                <div :class="isMobile ? 'col-12' : 'col-3'" class="row items-center no-wrap">
                  <q-toggle
                    v-model="editItem.is_serialized"
                    :label="editItem.is_serialized ? i18n.t('common.isSerialized') : i18n.t('common.generic')"
                    color="primary"
                    :disable="isSerializedDisabled"
                    @update:model-value="onIsSerializedUpdate"
                  />
                  <info-box
                    popup
                    class="q-ml-xs"
                    :tone="isSerializedBlocked ? infoBoxToneEnum.WARNING : infoBoxToneEnum.INFO"
                  >
                    <template v-if="isSerializedBlocked">{{ serializedTooltip }}</template>
                    <template v-else>
                      <div class="text-subtitle2 text-weight-bold">{{ i18n.t(`common.${stockTypeInfoKey}InfoTitle`) }}</div>
                      <div class="text-caption text-grey-8 q-mt-xs">{{ i18n.t(`common.${stockTypeInfoKey}InfoDescription`) }}</div>
                      <div class="text-subtitle2 text-weight-bold q-mt-sm">{{ i18n.t(`common.${stockTypeInfoKey}InfoWhenToUse`) }}</div>
                      <ul class="q-ma-none q-pl-md text-caption text-grey-8">
                        <li v-for="bullet in i18n.t(`common.${stockTypeInfoKey}InfoBullets`).split('|')" :key="bullet">{{ bullet }}</li>
                      </ul>
                    </template>
                  </info-box>
                </div>
                <q-checkbox
                  v-model="editItem.need_qr_scan"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.needQrScan')"
                  color="primary"
                />
                <q-checkbox
                  v-model="editItem.is_rented"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.isForRent')"
                  color="primary"
                  :disable="props.isRented || !isPhysical"
                  @update:model-value="onIsRentedUpdate"
                />
                <q-checkbox
                  v-model="editItem.is_vehicle"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.isVehicle')"
                  color="primary"
                  :disable="!isPhysical"
                  @update:model-value="onIsVehicleUpdate"
                />
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => { if (!success) return; if (isGroup) nextStep(1); else if (editItem.is_rented) nextStep(2); else nextStep(3) }) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.components')"
              icon="mdi-select-group"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
              :disable="!isGroup"
              :error="showComponentValidation && componentsHasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <service-selector
                  v-model="selectedComponentInput"
                  class="col-12"
                  :label="i18n.t('common.addComponent')"
                  :options="filteredComponents"
                  clearable
                  use-input
                  :input-debounce="webSiteStore.getInputDebounce()"
                  @filter="onComponentFilter"
                  @update:model-value="addComponent"
                />
              </div>
              <div v-if="showComponentValidation && isGroup && componentItems.length === 0" class="text-negative text-caption q-mt-sm">
                {{ getComponentsRequiredMessage() }}
              </div>
              <q-list
                v-if="componentItems.length"
                bordered
                separator
                class="q-mt-md rounded-borders"
              >
                <div
                  v-for="(item, index) in componentItems"
                  :key="item.id"
                  class="q-px-sm q-py-sm"
                >
                  <div class="row items-center q-col-gutter-sm">
                    <div :class="isMobile ? 'col-12' : 'col'">
                      <list-item
                        :avatar="item?.avatar_file || item?.avatar_url"
                        :label="getEquipmentName(item)"
                        :caption-chips="[...getServiceStructureTypeChip(item.structure_type), ...getTagChips(item.tags)]"
                        :right-caption-chips="getEquipmentRightCaptionChips(item)"
                        :right-chips="getEquipmentRightChips(item)"
                        icon="mdi-delete-outline"
                        icon-color="negative"
                        dense
                        @icon-click="removeComponent(item.id)"
                      />
                    </div>
                    <div :class="isMobile ? 'col-12' : 'col-3'">
                      <div class="row items-center no-wrap q-gutter-xs">
                        <q-btn
                          icon="mdi-minus"
                          round
                          dense
                          flat
                          color="primary"
                          @click="decrementComponentQuantity(item.id)"
                        />
                        <q-input
                          :model-value="item.component_quantity"
                          class="col"
                          type="number"
                          min="1"
                          step="1"
                          dense
                          outlined
                          :label="i18n.t('common.quantityAlt')"
                          @update:model-value="updateComponentQuantity(item.id, $event)"
                        />
                        <q-btn
                          icon="mdi-plus"
                          round
                          dense
                          flat
                          color="primary"
                          @click="incrementComponentQuantity(item.id)"
                        />
                      </div>
                    </div>
                  </div>
                  <q-separator v-if="index < componentItems.length - 1" class="q-mt-sm" />
                </div>
              </q-list>
              <stepper-navigation
                :continue="() => { if (!validateComboComponents({ notify: true })) return; if (editItem.is_rented) nextStep(1); else nextStep(2) }"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.rentalInfo')"
              icon="mdi-calendar-clock"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
              :disable="!editItem.is_rented"
              :error="wasAttempted(3) && rentalHasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <q-select
                  :model-value="editItem.rental_company"
                  use-input
                  :class="isMobile ? 'col-12' : 'col-6'"
                  new-value-mode="add-unique"
                  :label="i18n.t('common.company')"
                  option-value="id"
                  option-label="name"
                  :options="rentalCompanies"
                  :rules="[val => editItem.is_rented ? (val || i18n.t('common.required')) : true]"
                  @update:model-value="onRentalCompanySelected"
                />
                <q-input
                  :model-value="editItem.rental_company?.contact"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.contact')"
                  :disable="!editItem.rental_company"
                  @update:model-value="editItem.rental_company.contact = $event"
                />
                <q-input
                  :model-value="editItem.rental_company?.address"
                  :disable="!editItem.rental_company"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.address')"
                  autogrow
                  @update:model-value="editItem.rental_company.address = $event"
                />
                <q-field
                  v-model="editItem.availability"
                  borderless
                  :label="i18n.t('common.availability', 2)"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :rules="[val => editItem.is_rented ? (val && val.length > 0 || i18n.t('common.required')) : true]"
                >
                  <template #control>
                    <q-date
                      v-model="editItem.availability"
                      class="q-mt-lg full-width"
                      first-day-of-week="1"
                      multiple
                      range
                      today-btn
                      :landscape="!isMobile"
                    />
                  </template>
                </q-field>
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(3); form.validate().then((success) => (success ? nextStep(1) : null)) }"
                :back="() => { if (isGroup) prevStep(1); else prevStep(2) }"
              />
            </q-step>
            <q-step
              :name="4"
              :title="`${i18n.t('common.picture')}, ${i18n.t('common.barcode')} & ${i18n.t('common.condition', 2)}`"
              icon="mdi-calendar-clock"
              :done="!!editItem.id || step > 4"
              :header-nav="!!editItem.id || step > 4"
            >
              <div class="row q-col-gutter-sm">
                <div :class="isMobile ? 'col-6' : 'col-3'" class="flex flex-center">
                  <CameraGalleryInput ref="cameraGalleryInput" :edit-item="editItem" />
                  <q-btn
                    v-if="!editItem.avatar_file && !editItem.avatar_url"
                    icon="mdi-camera"
                    size="2rem"
                    round
                    @click="cameraGalleryInput.onActivateCamera()"
                  />
                  <q-avatar
                    v-else
                    size="10rem"
                    style="cursor: pointer;"
                    @click="cameraGalleryInput.onActivateCamera()"
                  >
                    <q-img
                      loading="lazy"
                      fit="contain"
                      :src="editItem.avatar_file?.payload || editItem.avatar_file || editItem.avatar_url"
                    >
                      <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                        <q-icon name="mdi-camera" size="2rem" />
                      </div>
                    </q-img>
                  </q-avatar>
                </div>
                <div :class="isMobile ? 'col-6' : 'col-3'" class="flex flex-center">
                  <qrcode-scanner
                    v-if="isScanning"
                    style="position: fixed; top: 0rem; left: 0rem; bottom:0; z-index: 5000; height: 100%; width: 100%;"
                    :timeout="5000"
                    :formats="null"
                    @detect="onDetect"
                    @timeout="onScanError"
                    @error="onScanError"
                  />
                  <q-btn
                    icon="mdi-qrcode-scan"
                    size="2rem"
                    round
                    :text-color="editItem.barcode ? 'positive' : 'black'"
                    :disabled="isScanning"
                    :loading="isScanning"
                    @click="onScan"
                  />
                </div>
                <div v-if="editItem.is_serialized" :class="isMobile ? 'col-12' : 'col-6'" class="column justify-center q-gutter-y-sm">
                  <condition-slider
                    v-model="editItem.condition_value"
                    :label="t('common.condition', 2)"
                  />
                  <q-input
                    v-model="editItem.condition_comment"
                    :label="t('common.comment', 2)"
                    autogrow
                  />
                </div>
              </div>
              <stepper-navigation
                :continue="() => nextStep(editItem.is_vehicle ? 1 : editItem.is_serialized ? 2 : 3)"
                :back="() => { if (editItem.is_rented) prevStep(1); else if (isGroup) prevStep(2); else prevStep(3) }"
              />
            </q-step>
            <q-step
              :name="5"
              :title="i18n.t('common.vehicleInfo')"
              icon="mdi-car"
              :done="!!editItem.id || step > 5"
              :header-nav="!!editItem.id || step > 5"
              :disable="!editItem.is_vehicle"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.license_plate"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.licensePlate')"
                />
                <q-input
                  v-model="editItem.km_traveled"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  type="number"
                  min="0"
                  :label="i18n.t('common.kmTraveled')"
                />
                <q-input
                  v-model="editItem.insurance"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.insurance')"
                  autogrow
                />
                <q-input
                  v-model="editItem.circulation_permits"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.circulationPermits')"
                  autogrow
                />
              </div>
              <stepper-navigation
                :continue="() => nextStep(editItem.is_serialized ? 1 : 2)"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="6"
              :title="i18n.t('common.serialInfo')"
              icon="mdi-barcode"
              :done="!!editItem.id || step > 6"
              :header-nav="!!editItem.id || step > 6"
              :disable="!editItem.is_serialized"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.serial_number"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.serialNumber')"
                  maxlength="20"
                  clearable
                />
                <q-input
                  v-model="editItem.internal_serial"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.internalSerial')"
                  maxlength="100"
                  clearable
                />
              </div>
              <stepper-navigation
                :continue="nextStep"
                :back="() => { if (editItem.is_vehicle) prevStep(1); else prevStep(2) }"
              />
            </q-step>
            <q-step
              :name="7"
              :title="i18n.t('common.additionalInfo')"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 7"
              :header-nav="!!editItem.id || step > 7"
            >
              <div class="row q-col-gutter-sm">
                <q-select
                  :model-value="editItem.currency_ref"
                  :class="isMobile ? 'col-6' : 'col-4'"
                  :label="i18n.t('common.currency')"
                  option-value="id"
                  option-label="code"
                  emit-value
                  map-options
                  clearable
                  :options="webSiteStore.currencies"
                  @update:model-value="onCurrencyChange"
                />
                <currency-input
                  :model-value="editItem.cost"
                  :class="isMobile ? 'col-6' : 'col-4'"
                  :label="i18n.t('common.cost')"
                  :suffix="currentCurrency.symbol"
                  @update:model-value="onCostChange"
                />
                <q-input
                  v-model="editItem.buy_date"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  clearable
                  mask="date"
                  :label="i18n.t('common.buyDate')"
                  :rules="[val => !val || !dayjs(val).isAfter(dayjs(), 'day') || i18n.t('validation.dateInFuture')]"
                >
                  <template #append>
                    <q-icon name="mdi-calendar" class="cursor-pointer">
                      <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                        <q-date
                          v-model="editItem.buy_date"
                          first-day-of-week="1"
                          :options="date => date <= dayjs().format('YYYY/MM/DD')"
                          today-btn
                        >
                          <div class="row items-center justify-end">
                            <q-btn
                              v-close-popup
                              :label="i18n.t('common.close')"
                              color="primary"
                              flat
                            />
                          </div>
                        </q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
                <exchange-rate-panel
                  v-if="editItem.currency_ref"
                  :model-value="pickExchangeRate(editItem)"
                  :currency-ref="editItem.currency_ref"
                  class="col-12"
                  @update:model-value="onExchangeRateChange"
                />
              </div>
              <file-url-edit
                bordered
                caption
                :model-value="editItem.service_files"
                class="q-mt-sm"
                :mobile="isMobile"
                @update:model-value="editItem.service_files = $event"
                @download="onDownloadAttachment"
              />
              <stepper-navigation
                :continue="() => { markAttempted(7); form.validate().then(success => success && nextStep()) }"
                :back="() => { if (editItem.is_serialized) prevStep(1); else if (editItem.is_vehicle) prevStep(2); else prevStep(3) }"
              />
            </q-step>
            <q-step
              :name="8"
              :title="i18n.t('common.rateCardInfo')"
              icon="mdi-currency-usd"
              :done="!!editItem.id || step > 8"
              :header-nav="!!editItem.id || step > 8"
            >
              <rate-card-info
                :model-value="editItem"
                :mobile="isMobile"
                @update:model-value="editItem = $event"
              />
              <info-box :id="infoBoxIdEnum.RATE_CARD_GENERIC_LISTING" class="q-mt-md">
                {{ i18n.t('common.rateCardGenericListingHint') }}
                <router-link :to="{ name: 'listings' }" class="text-primary text-weight-medium" style="text-decoration: none;">
                  {{ i18n.t('common.listing', 2) }}
                  <q-icon name="mdi-open-in-new" size="xs" />
                </router-link>
              </info-box>
              <stepper-navigation
                :back="prevStep"
                :continue="isPhysical ? () => nextStep() : null"
                :finish="!editItem.id && !isPhysical ? onSubmit : null"
                :finish-label="i18n.t('common.addEquipment')"
                :loading="isUpdating"
              />
            </q-step>
            <q-step
              :name="9"
              :title="`${i18n.t('common.warehouse')} & ${i18n.t('common.stock')}`"
              icon="mdi-warehouse"
              :done="!!editItem.id || step > 9"
              :header-nav="!!editItem.id || step > 9"
              :disable="!isPhysical"
            >
              <template v-if="!editItem.id">
                <div class="row q-col-gutter-sm items-center">
                  <warehouse-selector
                    v-model="editItem.warehouse"
                    :kind="planningStore.warehouseKindEnum.PHYSICAL"
                    :class="isMobile ? 'col-12' : 'col-6'"
                    :label="editItem.ordered ? i18n.t('common.destinationWarehouse') : i18n.t('common.referenceWarehouse')"
                    required
                  />
                  <div :class="isMobile ? 'col-12' : 'col-6'" class="row items-center no-wrap">
                    <q-input
                      v-model.number="editItem.warehouse_quantity"
                      class="col"
                      type="number"
                      min="1"
                      step="1"
                      :label="i18n.t('common.initialQuantity')"
                      :readonly="editItem.is_serialized"
                      :rules="[val => (val != null && val > 0) || i18n.t('common.required')]"
                    />
                    <info-box
                      v-if="editItem.is_serialized"
                      popup
                      class="q-ml-xs"
                      :message="i18n.t('common.serializedQuantityInfo')"
                    />
                  </div>
                </div>
                <equipment-order-fields
                  v-model:ordered="editItem.ordered"
                  v-model:order-warehouse="editItem.order_warehouse"
                  v-model:expected-arrival-date="editItem.expected_arrival_date"
                  :mobile="isMobile"
                />
              </template>
              <info-box v-if="editItem.id" :id="infoBoxIdEnum.MANAGE_STOCK_FROM_WAREHOUSE">
                {{ i18n.t('common.manageStockFromWarehouse') }}
                <router-link
                  :to="{ name: 'warehouse-list', query: { warehouse: editItem.warehouse, service: editItem.id } }"
                  target="_blank"
                  class="text-primary text-weight-medium"
                  style="text-decoration: none;"
                >
                  {{ i18n.t('common.warehouse') }}
                  <q-icon name="mdi-open-in-new" size="xs" />
                </router-link>
              </info-box>
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : onSubmit"
                :finish-label="i18n.t('common.addEquipment')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="editItem.name === ''"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSubmit"
      />
    </q-card-actions>
  </q-card>
</template>
<style scoped>
</style>
